import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch, faUsers, faBuilding,
  faRibbon, faChevronRight, faChevronDown, faXmark,
  faMedal, faGlobe, faFlag, faStar, faFire,
  faBolt, faRunning, faDumbbell, faLocationDot,
  faBasketball, faFutbol, faTableTennisPaddleBall,
  faVolleyball, faFlagCheckered,
  faUser, faPen,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../supabaseClient";
import { useAuth } from "../AuthContext";
import PublicSiteLayout from "./PublicSiteLayout";
import { SportMark } from "./SportLocationsArtwork";
import "./PublicRankings.css";
import { fetchDemoInstituteMemberRows, mergeDemoInstituteMembers } from "../demoInstituteMembers";

/* ═══════════════════════════════════════════
   THEME DEFINITIONS (same as LaunchPage)
═══════════════════════════════════════════ */
const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentDark: "#0A1D44",
  accentLight: "#0C69C8",
  accentGrad: "#075cdb",
  accentGlow: "rgba(12,105,200,0.25)",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.08)",
  accentBorder: "rgba(12,105,200,0.15)",
  bg: "#fafaf7",
  bgAlt: "#EDF0F5",
  bgCard: "#FFFFFF",
  bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)",
  borderMedium: "rgba(10,29,68,0.12)",
  shadowSm: "0 1px 3px rgba(10,29,68,0.06)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)",
  shadowLg: "0 12px 40px rgba(10,29,68,0.10)",
  text: "#10233e",
  textSecondary: "#59677a",
  textMuted: "#64748b",
  gold: "#B8860B",
  goldBg: "rgba(184,134,11,0.07)",
  goldBorder: "rgba(184,134,11,0.2)",
  success: "#059669",
  danger: "#DC2626",
  scrollbarThumb: "rgba(10,29,68,0.12)",
};

const EXCLUDED_IDS = [
  "1b824f5f-a287-4166-83e3-d6c2a9caa8e8",
  "580da2f2-f424-43d4-a859-304fa6d52915",
];

const cleanInstituteDisplayName = (name = "") =>
  String(name || "")
    .replace(/\s+(Students|Members|Departments|Teams)$/i, "")
    .trim();

const PAGE_SIZE = 50;

const SPORT_DISPLAY_ORDER = [
  "Basketball",
  "Football",
  "Tennis",
  "Padel",
  "Fitness",
  "Badminton",
  "Volleyball",
  "Futsal",
  "Boxing",
  "Track",
];

const SPORT_ICONS = {
  Basketball: faBasketball,
  Football: faFutbol,
  Tennis: faTableTennisPaddleBall,
  Padel: faTableTennisPaddleBall,
  Fitness: faRunning,
  Badminton: faTableTennisPaddleBall,
  Volleyball: faVolleyball,
  Futsal: faFutbol,
  Boxing: faDumbbell,
  Track: faFlagCheckered,
};

const sortSports = (sports = []) => [...sports].sort((a, b) => {
  const ai = SPORT_DISPLAY_ORDER.indexOf(a.id);
  const bi = SPORT_DISPLAY_ORDER.indexOf(b.id);
  if (ai !== -1 || bi !== -1) {
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  }
  return a.id.localeCompare(b.id);
});

const getSportIcon = (sport) => SPORT_ICONS[sport] || faMedal;

const RANK_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];
const TrophyEmoji = ({ size = 12, style = {} }) => (
  <span role="img" aria-label="trophies" style={{ fontSize: size, lineHeight: 1, ...style }}>🏆</span>
);

const formatPersonalBest = (ms) => {
  if (ms == null) return "–";
  const totalSeconds = ms / 1000;
  const mins = Math.floor(totalSeconds / 60);
  const secs = (totalSeconds % 60).toFixed(2).padStart(5, "0");
  return `${mins}:${secs}`;
};

// Public athlete profiles are addressed by username. Keep the ID fallback for
// older profiles that have not chosen a username yet.
const publicProfilePath = (entry) => {
  const username = String(entry?.username || "").trim().replace(/^@/, "");
  const identifier = username || entry?.id;
  return identifier ? `/profile/${encodeURIComponent(identifier)}` : "/leaderboard";
};

const noImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%23334155'/%3E%3Ccircle cx='20' cy='16' r='7' fill='%2394a3b8'/%3E%3Cellipse cx='20' cy='34' rx='12' ry='8' fill='%2394a3b8'/%3E%3C/svg%3E";
const isInstitutionInactive = (status) => ["cancelled", "expired", "revoked", "inactive"].includes(String(status || "").toLowerCase());
const readBrandSettings = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try { return JSON.parse(value); } catch { return {}; }
  }
  return typeof value === "object" ? value : {};
};
const isChallengeInstitute = (institute = {}) =>
  String(institute.institute_type || institute.type || "").toLowerCase() === "challenge";
const isGroupedInstitute = (institute = {}) => {
  const settings = readBrandSettings(institute.brand_settings);
  return Boolean(
    institute.hasTeams ||
    institute.teams ||
    settings.subgroups === true ||
    settings.subgroups === "true",
  );
};
const subgroupLabelForInstitute = (institute = {}) => {
  const settings = readBrandSettings(institute.brand_settings);
  const explicit = String(settings.subgroupLabel || settings.subgroup_label || "").toLowerCase();
  if (explicit.includes("department")) return "Departments";
  if (explicit.includes("team")) return "Teams";
  const type = String(institute.institute_type || institute.type || "").toLowerCase();
  const tier = String(institute.plan_tier || settings.planTier || "").toLowerCase();
  if (type === "company" || type === "school" || type === "college" || businessInstitutionPlanTiers.has(tier)) return "Departments";
  return "Teams";
};
const instituteModalRowRank = (institute = {}) => {
  if (isChallengeInstitute(institute)) return 3;
  if (isGroupedInstitute(institute)) return 2;
  return 1;
};
const businessInstitutionPlanTiers = new Set(["team", "startup", "growth", "scale", "enterprise"]);
const defaultAssociationTypeForInstitute = (institute = {}) => {
  const settings = readBrandSettings(institute.brand_settings);
  const type = String(institute.institute_type || institute.type || "").toLowerCase();
  if (type === "challenge") return "Member";
  if (type === "school" || type === "college") return "Student";
  const segment = String(settings.segment || settings.planSegment || "").toLowerCase();
  if (segment === "business") return "Employee";
  if (segment === "social" || segment === "club") return "Member";
  const tier = String(institute.plan_tier || settings.planTier || "").toLowerCase();
  if (businessInstitutionPlanTiers.has(tier) || type === "company") return "Employee";
  return "Member";
};

/* ═══════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════ */
const makeCSS = (C) => `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');




.lb-fade-in { animation: lbFadeIn 0.4s ease both; }
@keyframes lbFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

.lb-entry { transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease; cursor: pointer; }
.lb-entry:hover { transform: translateY(-2px); box-shadow: ${C.shadowMd}; }

.lb-pill { transition: all 0.18s ease; cursor: pointer; }
.lb-pill:hover { opacity: 0.85; }

.lb-sport-pill { transition: all 0.18s ease; cursor: pointer; white-space: nowrap; }
.lb-sport-pill:hover { opacity: 0.85; transform: translateY(-1px); }

.lb-back { transition: all 0.18s ease; cursor: pointer; }
.lb-back:hover { opacity: 0.75; transform: translateX(-2px); }

.lb-search-input { outline: none; }
.lb-search-input::placeholder { color: ${C.textMuted}; }

.lb-modal-overlay { animation: lbOverlayIn 0.22s ease both; }
@keyframes lbOverlayIn { from { opacity: 0; } to { opacity: 1; } }

.lb-modal { animation: lbModalIn 0.3s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes lbModalIn { from { opacity: 0; transform: scale(0.96) translateY(12px); } to { opacity: 1; transform: scale(1) translateY(0); } }

.lb-podium-card { transition: transform 0.25s ease; cursor: pointer; }
.lb-podium-card:hover { transform: translateY(-4px); }

 .public-rankings ::-webkit-scrollbar { width: 5px; height: 5px; }
 .public-rankings ::-webkit-scrollbar-track { background: transparent; }
 .public-rankings ::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }

.lb-stagger > *:nth-child(1) { animation-delay: 0.04s; }
.lb-stagger > *:nth-child(2) { animation-delay: 0.08s; }
.lb-stagger > *:nth-child(3) { animation-delay: 0.12s; }
.lb-stagger > *:nth-child(4) { animation-delay: 0.16s; }
.lb-stagger > *:nth-child(5) { animation-delay: 0.20s; }
.lb-stagger > *:nth-child(n+6) { animation-delay: 0.24s; }

@keyframes lbPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.lb-skeleton { background: ${C.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(10,29,68,0.06)"}; border-radius: 8px; animation: lbPulse 1.5s ease infinite; }
`;

/* ═══════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════ */
const Avatar = ({ src, size = 44, round = true, style = {} }) => (
  <img
    src={src || noImage}
    alt=""
    onError={e => { e.target.src = noImage; }}
    style={{
      width: size, height: size, objectFit: "cover", flexShrink: 0,
      borderRadius: round ? "50%" : 10,
      background: "rgba(128,128,128,0.1)",
      ...style,
    }}
  />
);

const COUNTRY_CODE_BY_NAME = {
  argentina: "AR", australia: "AU", belgium: "BE", brazil: "BR", canada: "CA", china: "CN", denmark: "DK",
  england: "GB", finland: "FI", france: "FR", germany: "DE", india: "IN", ireland: "IE", italy: "IT", japan: "JP",
  mexico: "MX", morocco: "MA", netherlands: "NL", "new zealand": "NZ", norway: "NO", poland: "PL", portugal: "PT",
  scotland: "GB", singapore: "SG", "south africa": "ZA", "south korea": "KR", spain: "ES", sweden: "SE",
  switzerland: "CH", turkey: "TR", "united arab emirates": "AE", "united kingdom": "GB", "united states": "US",
  "united states of america": "US", usa: "US", wales: "GB",
};
const countryFlagEmoji = (country) => {
  const name = String(country || "").trim();
  if (!name) return "";
  const code = /^[A-Za-z]{2}$/.test(name) ? name.toUpperCase() : COUNTRY_CODE_BY_NAME[name.toLowerCase()];
  return /^[A-Z]{2}$/.test(code || "")
    ? code.split("").map(letter => String.fromCodePoint(127397 + letter.charCodeAt(0))).join("")
    : "";
};
const countryWithFlag = (country) => {
  const name = String(country || "").trim();
  if (!name) return "";
  const flag = countryFlagEmoji(name);
  return flag ? `${name} ${flag}` : name;
};

const ScopePill = ({ label, active, onClick, C, gold }) => (
  <button className="lb-pill" onClick={onClick} aria-label={label} aria-pressed={active} style={{
    padding: "7px 16px", borderRadius: 99,
    border: `1px solid ${active ? (gold ? C.gold : C.accent) : C.border}`,
    background: active ? (gold ? C.goldBg : C.accentBgLight) : "transparent",
    color: active ? (gold ? C.gold : C.accent) : C.textMuted,
    fontSize: 12, fontWeight: 700, cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
  }}>
    {label}
  </button>
);

const Skeleton = ({ w = "100%", h = 16, style = {} }) => (
  <div className="lb-skeleton" style={{ width: w, height: h, ...style }} />
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const Leaderboard = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  // ── Theme ──
  const isDark = false;
  const C = LIGHT;

  // ── Sport ──
  const [availableSports, setAvailableSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);
  const [sportsError, setSportsError] = useState("");
  const [selectedSport, setSelectedSport] = useState("");
  const isTrack = selectedSport === "Track";
  const isFitness = selectedSport === "Fitness" || selectedSport === "Track";
  const dublinToday = useMemo(() => new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Dublin", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date()), []);

  const fetchActiveSports = useCallback(async () => {
    setSportsLoading(true);
    setSportsError("");
    try {
      const { data, error } = await supabase
        .from("sports")
        .select("id")
        .eq("active", true)
        .order("id");

      if (error) throw error;

      const nextSports = sortSports((data || [])
        .filter(s => s?.id)
        .map(s => ({ id: s.id, icon: getSportIcon(s.id) })));

      setAvailableSports(nextSports);
      setSelectedSport(current => (
        nextSports.some(s => s.id === current) ? current : (nextSports[0]?.id || "")
      ));
    } catch (err) {
      console.error("fetchActiveSports error:", err.message);
      setSportsError("Supported sports could not be loaded. Try again.");
      setAvailableSports([]);
      setSelectedSport("");
    } finally {
      setSportsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveSports();
  }, [fetchActiveSports]);

  // ── Scope ──
  const [scope, setScope] = useState("Global"); // Region | National | Global

  // ── Location for region/national filtering ──
  const [locationBanner, setLocationBanner] = useState(null); // null | "requesting" | "granted" | "denied" | "manual"
  const [manualRegion, setManualRegion] = useState("");
  const [manualCountry, setManualCountry] = useState("");
  const [manualInput, setManualInput] = useState("");
  const [locationResolved, setLocationResolved] = useState(false);

  // Try geolocation once on mount
  useEffect(() => {
    // If profile already has region/country, we're fine
    if (profile?.region && profile?.current_country) {
      setLocationResolved(true);
      return;
    }
    // Otherwise, show banner when user selects Region or National
  }, [profile]);

  const requestLocation = useCallback(() => {
    setLocationBanner("requesting");
    if (!navigator.geolocation) {
      setLocationBanner("manual");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          const country = data.address?.country || "";
          const region = data.address?.state || data.address?.county || "";
          setManualRegion(region);
          setManualCountry(country);
          setLocationResolved(true);
          setLocationBanner("granted");
        } catch {
          setLocationBanner("manual");
        }
      },
      () => setLocationBanner("manual")
    );
  }, []);

  const handleManualSubmit = useCallback(() => {
    if (!manualInput.trim()) return;
    // Simple heuristic: if scope is Region treat as region, else country
    if (scope === "Region") setManualRegion(manualInput.trim());
    else setManualCountry(manualInput.trim());
    setLocationResolved(true);
    setLocationBanner(null);
  }, [manualInput, scope]);

  // Effective region/country = profile values OR manually resolved
  const effectiveRegion = profile?.region || manualRegion;
  const effectiveCountry = profile?.current_country || profile?.country || manualCountry;
  const scopeLocation = scope === "Region"
    ? [effectiveRegion, countryFlagEmoji(effectiveCountry)].filter(Boolean).join(" ")
    : scope === "National"
      ? countryWithFlag(effectiveCountry)
      : "";

  // Show location banner when scope !== Global and no location yet
  const needsLocation = (scope === "Region" || scope === "National") && !locationResolved && !profile?.region;

  // ── View mode ──
  const [showTeams, setShowTeams] = useState(false);
  const [showInstitute, setShowInstitute] = useState(false);

  // ── Data ──
  const [profiles, setProfiles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [liveRankMovementById, setLiveRankMovementById] = useState({});

  // ── Search modal ──
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);

  // ── Institute modal ──
  const [instituteOpen, setInstituteOpen] = useState(false);
  const modalRef = useRef(null);
  useEffect(() => {
    if (!searchOpen && !instituteOpen) return undefined;
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusable = () => Array.from(modalRef.current?.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex="0"]') || []);
    const focusTimer = window.setTimeout(() => focusable()[0]?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setSearchOpen(false); setInstituteOpen(false); }
      if (event.key !== "Tab") return;
      const nodes = focusable();
      if (!nodes.length) { event.preventDefault(); return; }
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || !modalRef.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !modalRef.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => { window.clearTimeout(focusTimer); document.body.style.overflow = previousOverflow; window.removeEventListener("keydown", onKeyDown, true); if (previous?.isConnected) previous.focus(); };
  }, [searchOpen, instituteOpen]);
  const [userInstitutes, setUserInstitutes] = useState([]);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [instituteUsers, setInstituteUsers] = useState([]);
  const [instituteTeams, setInstituteTeams] = useState([]);
  const [instituteLoading, setInstituteLoading] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [joinError, setJoinError] = useState("");

  // ── Dept ──
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptMembers, setDeptMembers] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);

  // ── Season modal ──
  const [seasonModal, setSeasonModal] = useState(null);

  // ── Mobile ──
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	  useEffect(() => {
	    const h = () => setIsMobile(window.innerWidth <= 768);
	    window.addEventListener("resize", h);
	    return () => window.removeEventListener("resize", h);
	  }, []);

  const instituteModalGroups = useMemo(() => {
    const instituteById = new Map(userInstitutes.filter(inst => inst?.id).map(inst => [inst.id, inst]));
    const groups = new Map();

    userInstitutes.forEach(inst => {
      if (!inst?.id) return;
      const settings = readBrandSettings(inst.brand_settings);
      const parentId = inst.auto_join_institute_id || settings.parentInstituteId || null;
      const parent = parentId ? instituteById.get(parentId) : null;
      const key = inst.workspace_id || parent?.workspace_id || parentId || `single:${inst.id}`;
      if (!groups.has(key)) groups.set(key, { key, rows: [] });
      groups.get(key).rows.push(inst);
    });

    return Array.from(groups.values())
      .map(group => {
        const rows = [...group.rows].sort((a, b) => {
          const rankDiff = instituteModalRowRank(a) - instituteModalRowRank(b);
          if (rankDiff !== 0) return rankDiff;
          return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
        });
        const primary = rows.find(inst => !isChallengeInstitute(inst) && !isGroupedInstitute(inst))
          || rows.find(inst => !isChallengeInstitute(inst))
          || rows[0];
        return {
          ...group,
          title: cleanInstituteDisplayName(primary?.rawName || primary?.name) || primary?.name || "Institute",
          image: primary?.image || "",
          rows,
        };
      })
      .sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), undefined, { sensitivity: "base" }));
  }, [userInstitutes]);

	  // ── Fetch leaderboard ──
  const fetchData = useCallback(async (pg = 0, sport = selectedSport) => {
    setDataError("");
    if (!sport) {
      setProfiles([]);
      setTeams([]);
      setLoading(false);
      setLoadingMore(false);
      setPage(0);
      setHasMore(false);
      return;
    }

    try {
      if (pg === 0) setLoading(true);
      else setLoadingMore(true);

      const from = pg * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const profilesQuery = supabase.from("sport_profiles")
        .select(`profile_id, sport_id, league, rating, trophies, todays_steps, last_steps_date,
          season_steps, regional_rank, national_rank, global_rank, personal_best,
          profiles!inner(id, username, name, surname, region, level, current_country, profile_img, date_of_birth)`)
        .eq("sport_id", sport)
        .eq("profiles.show_on_leaderboards", true)
        .not("profiles.id", "in", `(${EXCLUDED_IDS.join(",")})`)
        .range(from, to)
        .order("trophies", { ascending: false });
      const { data: profilesData, error } = await profilesQuery;

      if (error) throw error;

      if (pg === 0 && sport === "Fitness") {
        const { data: movementData, error: movementError } = await supabase.rpc("get_fitness_live_rank_movement");
        if (movementError) {
          console.warn("Could not load live Fitness rank movement:", movementError.message);
          setLiveRankMovementById({});
        } else {
          setLiveRankMovementById(Object.fromEntries(
            (movementData || [])
              .filter(row => Number(row.rank_movement || 0) !== 0)
              .map(row => [String(row.entry_id), Number(row.rank_movement)]),
          ));
        }
      } else if (pg === 0) {
        setLiveRankMovementById({});
      }

      const mapped = (profilesData || []).map(({ profiles: p, ...sp }) => ({ ...(p || {}), profileImg: p?.profile_img, ...sp, id: p?.id }));

      setProfiles(prev => pg === 0 ? mapped : [...prev, ...mapped]);
      if ((profilesData || []).length < PAGE_SIZE) setHasMore(false);
      else setHasMore(true);

      // Teams (only on first page)
      if (pg === 0) {
        const { data: teamsData } = await supabase
          .from("teams")
          .select(`id, creator_id, admin, team_name, team_type, image_url, league, rating,
            trophies, regional_rank, national_rank, global_rank, sport_id,
            profiles!admin(region, current_country),
            user_teams(profile_id, profiles(id, profile_img))`)
          .eq("sport_id", sport);

        const mapped2 = (teamsData || []).map(t => ({
          id: t.id,
          teamName: t.team_name,
          teamType: t.team_type,
          teamImage: t.image_url,
          league: t.league,
          rating: t.rating,
          trophies: t.trophies,
          regional_rank: t.regional_rank,
          national_rank: t.national_rank,
          global_rank: t.global_rank,
          region: t.profiles?.region ?? null,
          current_country: t.profiles?.current_country ?? null,
          memberImages: (t.user_teams || [])
            .filter(ut => !EXCLUDED_IDS.includes(ut.profiles?.id))
            .map(ut => ut.profiles?.profile_img).filter(Boolean),
        }));
        setTeams(mapped2);
      }
    } catch (err) {
      console.error("fetchData error:", err.message);
      setDataError("Rankings could not load. Check your connection and try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setPage(pg);
    }
  }, [selectedSport]);

  useEffect(() => {
    setProfiles([]); setTeams([]); setPage(0); setHasMore(true);
    setShowTeams(false); setShowInstitute(false); setSelectedInstitute(null);
    if (!selectedSport) {
      setLoading(false);
      setHasMore(false);
      return;
    }
    fetchData(0, selectedSport);
  }, [selectedSport, fetchData]);

  // Live bot totals are computed on the server from the configured time window.
  // A light refresh keeps a Fitness leaderboard open in the browser current.
  useEffect(() => {
    if (selectedSport !== "Fitness") return undefined;
    const interval = window.setInterval(() => fetchData(0, selectedSport), 60 * 1000);
    return () => window.clearInterval(interval);
  }, [fetchData, selectedSport]);

  // ── Fetch user institutes ──
  const fetchUserInstitutes = useCallback(async () => {
    if (!user?.id) return;
    try {
	      const { data } = await supabase
	        .from("user_institutes")
	        .select(`institute_id, steps, lifetime_steps, team_id, association_type, status,
	          institutes(id, name, country, state_province, image_url, brand_logo_url, access_code, institute_type, is_active, archived_at, billing_status,
              brand_settings, plan_tier, teams, workspace_id, auto_join_institute_id,
	            user_institutes(steps, lifetime_steps, status))`)
	        .eq("profile_id", user.id)
	        .eq("status", "Active");

      const map = new Map();
      (data || []).forEach(inst => {
        const i = inst.institutes;
        if (!i?.id || i.archived_at || i.is_active === false) return;
        if (isInstitutionInactive(i.billing_status)) return;
        const active = (i.user_institutes || []).filter(u => u.status === "Active");
        const totalSteps = active.reduce((s, e) => s + (e.steps || 0), 0);
        if (!map.has(i.id)) {
	          const brandSettings = readBrandSettings(i.brand_settings);
	          map.set(i.id, {
	            id: i.id, name: cleanInstituteDisplayName(i.name) || i.name, rawName: i.name, image: i.brand_logo_url || i.image_url,
	            state_province: i.state_province, country: i.country,
	            institute_type: i.institute_type, access_code: i.access_code,
              brand_settings: brandSettings, plan_tier: i.plan_tier,
              teams: i.teams, hasTeams: Boolean(i.teams || brandSettings.subgroups === true || brandSettings.subgroups === "true"),
              workspace_id: i.workspace_id, auto_join_institute_id: i.auto_join_institute_id,
	            steps: inst.steps ?? 0, institute_steps: totalSteps,
	            team_id: inst.team_id,
	          });
        }
      });
      setUserInstitutes(Array.from(map.values()));
    } catch (err) {
      console.error("fetchUserInstitutes:", err);
    }
  }, [user?.id]);

  useEffect(() => { fetchUserInstitutes(); }, [fetchUserInstitutes]);

  // ── Fetch institute detail ──
  const fetchInstituteDetail = useCallback(async (instituteId) => {
    setInstituteLoading(true);
    try {
      const { data: instData } = await supabase
        .from("institutes_with_steps")
        .select("*")
        .eq("id", instituteId)
        .single();

      if (!instData) return;

      setSelectedInstitute({
        id: instData.id, name: cleanInstituteDisplayName(instData.name) || instData.name, image: instData.brand_logo_url || instData.image_url,
        state_province: instData.state_province, country: instData.country,
        institute_type: instData.institute_type, totalSteps: instData.total_steps || 0,
        hasTeams: false,
      });

      const { data: teamsData } = await supabase
        .from("institute_teams_with_steps")
        .select("*")
        .eq("institute_id", instituteId)
        .order("total_steps", { ascending: false });

      let usersQuery = supabase
        .from("user_institutes")
        .select("profile_id, steps, association_type, profiles!inner(name, surname, username, level, profile_img)")
        .eq("institute_id", instituteId)
        .eq("profiles.show_on_leaderboards", true)
        .order("steps", { ascending: false });

      if (instData.institute_type === "School" || instData.institute_type === "College") {
        usersQuery = usersQuery.eq("association_type", "Student");
      }
      const { data: usersData } = await usersQuery;
      const demoRows = await fetchDemoInstituteMemberRows(supabase, { instituteId, activeOnly: true });

      const t = (teamsData || []).map(t => ({ id: t.id, teamName: t.name, teamImage: t.image_url, totalSteps: t.total_steps, teamType: "group" }));
      const u = mergeDemoInstituteMembers(usersData || [], demoRows).map(u => ({
        id: u.profile_id || u.demo_profile_id, name: u.profiles.name, surname: u.profiles.surname,
        username: u.profiles.username, level: u.profiles.level,
        profileImg: u.profiles.profile_img || "", totalSteps: u.steps || 0,
      })).sort((a, b) => Number(b.totalSteps || 0) - Number(a.totalSteps || 0));

      setInstituteTeams(t);
      setInstituteUsers(u);
      if (t.length > 0) setSelectedInstitute(prev => ({ ...prev, hasTeams: true }));
    } catch (err) {
      console.error("fetchInstituteDetail:", err);
    } finally {
      setInstituteLoading(false);
    }
  }, []);

  // ── Fetch dept members ──
  const fetchDeptMembers = useCallback(async (teamId, teamName, teamImage) => {
    setDeptLoading(true);
    setSelectedDept({ id: teamId, name: teamName, image: teamImage });
    try {
      const { data } = await supabase
        .from("user_institutes")
        .select("profile_id, steps, profiles!inner(id, name, surname, username, level, profile_img)")
        .eq("team_id", teamId)
        .eq("status", "Active")
        .eq("profiles.show_on_leaderboards", true)
        .not("profile_id", "in", `(${EXCLUDED_IDS.join(",")})`)
        .order("steps", { ascending: false });
      const demoRows = await fetchDemoInstituteMemberRows(supabase, { teamId, activeOnly: true });

      setDeptMembers(mergeDemoInstituteMembers(data || [], demoRows).map(u => ({
        id: u.profile_id || u.demo_profile_id, name: u.profiles.name, surname: u.profiles.surname,
        username: u.profiles.username, level: u.profiles.level,
        profileImg: u.profiles.profile_img || "", totalSteps: u.steps || 0,
      })).sort((a, b) => Number(b.totalSteps || 0) - Number(a.totalSteps || 0)));
    } catch (err) { console.error("fetchDeptMembers:", err); }
    finally { setDeptLoading(false); }
  }, []);

  // ── Join institute ──
  const handleJoin = useCallback(async () => {
    if (!accessCode.trim()) { setJoinError("Enter an access code."); return; }
    try {
      const { data: instMatch } = await supabase
        .from("institutes").select("id,name,institute_type,billing_status,brand_settings,plan_tier").eq("access_code", accessCode.trim()).single();
      if (!instMatch) { setJoinError("Invalid access code."); return; }
      if (isInstitutionInactive(instMatch.billing_status)) {
        setJoinError("This institute is inactive due to billing or payment issues. Ask an admin to restart the plan.");
        return;
      }

      const { data: existing } = await supabase
        .from("user_institutes")
        .select("id,status")
        .eq("profile_id", user.id)
        .eq("institute_id", instMatch.id)
        .maybeSingle();

      if (existing?.status === "Active") { setJoinError("You've already joined this institute."); return; }

      if (existing) {
        await supabase.from("user_institutes").update({ status: "Active", left_date: null }).eq("id", existing.id);
      } else {
        await supabase.from("user_institutes").insert([{
          profile_id: user.id, institute_id: instMatch.id,
          association_type: defaultAssociationTypeForInstitute(instMatch), status: "Active",
          joined_date: new Date().toISOString(), lifetime_steps: 0,
        }]);
      }
      setAccessCode(""); setJoinError("");
      fetchUserInstitutes();
    } catch (err) {
      setJoinError("An error occurred. Try again.");
    }
  }, [accessCode, user?.id, fetchUserInstitutes]);

  // ── Search ──
  const handleSearch = useCallback((q) => {
    setSearchQuery(q);
    const source = showTeams ? teams : profiles;
    const lower = q.toLowerCase();
    setSearchResults(
      source.filter(item =>
        showTeams
          ? item.teamName?.toLowerCase().includes(lower)
          : (item.username?.toLowerCase().includes(lower) ||
            `${item.name} ${item.surname}`.toLowerCase().includes(lower))
      ).slice(0, 30)
    );
  }, [showTeams, teams, profiles]);

  useEffect(() => {
    if (searchOpen) {
      setSearchResults(showTeams ? teams.slice(0, 30) : profiles.slice(0, 30));
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [searchOpen, showTeams, teams, profiles]);

  // ── Filtered leaderboard ──
  const filtered = useMemo(() => {
    if (selectedDept) return [...deptMembers].sort((a, b) => (b.totalSteps || 0) - (a.totalSteps || 0));
    if (showInstitute && selectedInstitute) {
      const data = selectedInstitute.hasTeams ? instituteTeams : instituteUsers;
      return [...data].sort((a, b) => (b.totalSteps || 0) - (a.totalSteps || 0));
    }
    const source = showTeams ? teams : profiles;
    return source.filter(e =>
      e &&
      !EXCLUDED_IDS.includes(e.id) &&
      (scope === "Region"
        ? e.region && effectiveRegion && e.region.toLowerCase() === effectiveRegion.toLowerCase()
        : scope === "National"
        ? e.current_country?.toLowerCase() === effectiveCountry?.toLowerCase()
        : true)
    ).sort((a, b) =>
      selectedSport === "Fitness"
        ? Number(b.trophies || 0) - Number(a.trophies || 0)
        : isTrack
        ? (a.personal_best ?? Infinity) - (b.personal_best ?? Infinity)
        : b.trophies - a.trophies
    );
  }, [selectedDept, deptMembers, showInstitute, selectedInstitute, instituteTeams, instituteUsers,
    showTeams, teams, profiles, scope, isTrack, selectedSport, effectiveRegion, effectiveCountry]);

  // ── Infinite scroll ──
  const listRef = useRef(null);
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore || showInstitute || selectedDept) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      fetchData(page + 1, selectedSport);
    }
  }, [loadingMore, hasMore, showInstitute, selectedDept, page, selectedSport, fetchData]);

  // ── Render podium ──
  // ── Render entry row ──
  const renderEntry = (entry, index) => {
    const rank = index + 1;
    const rankMovement = liveRankMovementById[String(entry.id)] || 0;
    const showsRankMovement = selectedSport === "Fitness" && !showTeams && !showInstitute && !selectedDept && rankMovement !== 0;
    const rankMovementUp = rankMovement > 0;
    const isTop3 = rank <= 3;
    const rankColor = isTop3 ? RANK_COLORS[rank - 1] : null;

    const getName = () => {
      if (selectedDept) return `${entry.name} ${entry.surname || ""}`.trim();
      if (showTeams) return entry.teamName;
      if (showInstitute) return entry.name || entry.teamName;
      return `${entry.name || ""} ${entry.surname || ""}`.trim();
    };

    const getMeta = () => {
      if (selectedDept) return `@${entry.username} · Lv ${entry.level}`;
      if (showTeams) return `League ${entry.league || "–"}`;
      if (showInstitute) return entry.state_province || "";
      return `@${entry.username}`;
    };

    const getScore = () => {
      if (selectedDept || showInstitute) return `${(entry.totalSteps || 0).toLocaleString()} steps`;
      if (selectedSport === "Fitness") return `${(entry.trophies || 0).toLocaleString()} steps`;
      if (isTrack) return formatPersonalBest(entry.personal_best);
      return (entry.trophies || 0).toLocaleString();
    };

    const getImg = () => entry.profileImg || entry.teamImage || entry.image || noImage;
    const isRect = entry.teamType === "group" || (showInstitute && !showTeams);
    const canNavigate = (!showInstitute || selectedDept);
    const isDept = showInstitute && selectedInstitute?.hasTeams && !selectedDept && entry.teamType === "group";

    return (
      <div key={entry.id} className="lb-entry lb-fade-in" role={(canNavigate || isDept) ? "button" : undefined} tabIndex={(canNavigate || isDept) ? 0 : undefined} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { if (!(canNavigate || isDept)) return; event.preventDefault(); event.currentTarget.click(); } }}
        onClick={() => {
          if (isDept) { fetchDeptMembers(entry.id, entry.teamName, entry.teamImage); }
          else if (canNavigate) { navigate(showTeams ? `/viewscreen/team/${entry.id}` : publicProfilePath(entry)); }
        }}
        style={{
          display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
          borderRadius: 14,
          background: C.bgCard,
          border: `1px solid ${C.border}`,
          boxShadow: "none",
          cursor: (canNavigate || isDept) ? "pointer" : "default",
        }}>
        {/* Rank badge */}
        <div style={{ width: 32, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: isTop3 ? `${RANK_COLORS[rank - 1]}22` : C.bgAlt,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: isTop3 ? `1px solid ${RANK_COLORS[rank - 1]}55` : `1px solid ${C.border}`,
          }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: isTop3 ? RANK_COLORS[rank - 1] : C.textMuted, fontFamily: "'Sora', sans-serif" }}>{rank}</span>
          </div>
          {showsRankMovement && (
            <span
              aria-label={`${rankMovementUp ? "Up" : "Down"} ${Math.abs(rankMovement)} ${Math.abs(rankMovement) === 1 ? "place" : "places"} since the last update`}
              title={`${rankMovementUp ? "Up" : "Down"} ${Math.abs(rankMovement)} ${Math.abs(rankMovement) === 1 ? "place" : "places"} since the last update`}
              style={{ marginTop: 2, fontSize: 10, fontWeight: 900, color: rankMovementUp ? C.success : C.danger, fontVariantNumeric: "tabular-nums" }}
            >
              {rankMovementUp ? "↑" : "↓"}{Math.abs(rankMovement)}
            </span>
          )}
        </div>

        {/* Avatar */}
        {entry.teamType === "individual" && entry.memberImages?.length > 0
          ? <div style={{ display: "flex", flexShrink: 0 }}>
              {entry.memberImages.slice(0, 3).map((img, i) => (
                <img key={i} src={img || noImage} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", marginLeft: i > 0 ? -10 : 0, border: `2px solid ${C.bgCard}` }} onError={e => e.target.src = noImage} />
              ))}
            </div>
          : <img src={getImg()} alt="" onError={e => e.target.src = noImage}
              style={{ width: isRect ? 56 : 42, height: 42, borderRadius: isRect ? 8 : "50%", objectFit: "cover", flexShrink: 0 }} />
        }

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{getName()}</div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
            {getMeta()}
          </div>
        </div>

        {/* Score */}
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, fontFamily: "'Sora', sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 5 }}>
            <span>{getScore()}</span>
            {!showInstitute && !isTrack && selectedSport !== "Fitness" && !selectedDept && <TrophyEmoji size={10} />}
          </div>
          {!showInstitute && !isTrack && !selectedDept && entry.todays_steps > 0 && entry.last_steps_date === dublinToday && (
            <div style={{ fontSize: 10, fontWeight: 700, color: C.success, marginTop: 2 }}>+{entry.todays_steps} today</div>
          )}
          {isDept && <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }} />}
        </div>
      </div>
    );
  };

  // ── Page title ──
  const pageTitle = selectedDept
    ? selectedDept.name
    : showInstitute && selectedInstitute
    ? selectedInstitute.name
    : selectedSport || "Leaderboard";
  const pageIcon = !selectedDept && !(showInstitute && selectedInstitute) ? getSportIcon(selectedSport) : null;

  const pageSubtitle = selectedDept
    ? "Department Members"
    : showInstitute && selectedInstitute
    ? `${(selectedInstitute.totalSteps || 0).toLocaleString()} total steps`
    : showTeams ? "Teams" : selectedSport === "Fitness" ? "Monthly season steps" : "Athletes";

  return (
    <PublicSiteLayout className="public-rankings">
      <style>{makeCSS(C)}</style>

      {/* ── HEADER ── */}
      <div className="ps-wrap pr-hero"><div><span className="pr-label">Rankings</span><h1>Sports leaderboards.</h1><p>Explore athletes and teams by sport. Follow the monthly Fitness step season or find your community’s rankings.</p></div><button type="button" className="ps-button ps-button-secondary" onClick={() => setSearchOpen(true)}><FontAwesomeIcon icon={faSearch} aria-hidden="true" />Search rankings</button></div>
      <div className="ps-wrap pr-view"><div><h2>{pageIcon && <SportMark sport={selectedSport} />}{pageTitle}</h2><p>{pageSubtitle}</p></div></div>

      <div className="ps-wrap pr-board">

        {/* ── SPORT SELECTOR (centered) ── */}
        {!showInstitute && !selectedDept && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-start" }}>
              {sportsLoading ? (
                [...Array(4)].map((_, i) => (
                  <Skeleton key={i} w={86} h={32} style={{ borderRadius: 99 }} />
                ))
              ) : sportsError ? (
                <div role="status" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 10, color: C.textMuted, fontSize: 13 }}>
                  <span>{sportsError}</span>
                  <button type="button" className="lb-pill" onClick={fetchActiveSports} style={{
                    padding: "7px 14px", borderRadius: 99, border: `1px solid ${C.accent}`,
                    background: C.accentBgLight, color: C.accent, fontSize: 12, fontWeight: 800,
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  }}>
                    Retry
                  </button>
                </div>
              ) : availableSports.length === 0 ? (
                <div role="status" style={{ color: C.textMuted, fontSize: 13 }}>No sports are currently available.</div>
              ) : (
                availableSports.map(s => (
                  <button key={s.id} className="lb-sport-pill" type="button" aria-pressed={selectedSport === s.id} onClick={() => setSelectedSport(s.id)} style={{
                    padding: "7px 14px", borderRadius: 99, fontSize: 12, fontWeight: 700,
                    border: `1px solid ${selectedSport === s.id ? C.accent : C.border}`,
                    background: selectedSport === s.id ? C.accentBgLight : C.bgCard,
                    color: selectedSport === s.id ? C.accent : C.textMuted,
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <SportMark sport={s.id} /> {s.id}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── SCOPE + TOGGLE BAR (centered) ── */}
        {!selectedDept && !showInstitute && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, marginBottom: 2 }}>
            {/* Scope pills row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-start", flexWrap: "wrap" }}>
              {["Region", "National", "Global"].map(s => (
                <ScopePill
                  key={s}
                  label={s}
                  active={scope === s}
                  onClick={() => {
                  setScope(s);
                  // If switching to Region/National and no location, prompt
                  if ((s === "Region" || s === "National") && !locationResolved && !profile?.region) {
                    setLocationBanner("prompt");
                  }
                }}
                  C={C}
                />
              ))}
              {!isFitness && !isTrack && (
                <button className="lb-pill" onClick={() => setShowTeams(v => !v)} style={{
                  padding: "7px 16px", borderRadius: 99, border: `1px solid ${C.goldBorder}`,
                  background: showTeams ? C.goldBg : "transparent",
                  color: C.gold, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  <FontAwesomeIcon icon={showTeams ? faUser : faUsers} aria-hidden="true" style={{ marginRight: 6 }} />
                  {showTeams ? "Users" : "Teams"}
                </button>
              )}
            </div>

            <div style={{ height: 13, fontSize: 11, fontWeight: 700, color: C.textMuted, textAlign: "center", marginTop: -3 }}>
              {scopeLocation}
            </div>

            {/* Location banner */}
            {(scope === "Region" || scope === "National") && !locationResolved && !profile?.region && (
              <div style={{
                width: "100%", maxWidth: 480,
                background: C.accentBgSubtle,
                border: `1px solid ${C.accentBorder}`,
                borderRadius: 14,
                padding: "12px 16px",
                display: "flex", flexDirection: "column", gap: 10,
              }}>
                {locationBanner !== "manual" ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 10, background: C.accentBgLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FontAwesomeIcon icon={faLocationDot} style={{ color: C.accent, fontSize: 14 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                          {scope === "Region" ? "Region filter needs your location" : "Country filter needs your location"}
                        </div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1 }}>
                          Used only to filter the leaderboard — never stored.
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={requestLocation} style={{
                        flex: 1, padding: "9px 0", borderRadius: 10, border: "none",
                        background: C.accentGrad, color: "#fff", fontSize: 12, fontWeight: 700,
                        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                      }}>
                        <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" style={{ marginRight: 6 }} />
                        Use My Location
                      </button>
                      <button onClick={() => setLocationBanner("manual")} style={{
                        flex: 1, padding: "9px 0", borderRadius: 10,
                        border: `1px solid ${C.border}`,
                        background: C.bgCard, color: C.textMuted, fontSize: 12, fontWeight: 700,
                        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                      }}>
                        <FontAwesomeIcon icon={faPen} aria-hidden="true" style={{ marginRight: 6 }} />
                        Enter Manually
                      </button>
                    </div>
                    {locationBanner === "requesting" && (
                      <div style={{ fontSize: 11, color: C.textMuted, textAlign: "center" }}>Requesting location…</div>
                    )}
                    {locationBanner === "denied" && (
                      <div style={{ fontSize: 11, color: C.danger, textAlign: "center" }}>Location denied. Please enter manually.</div>
                    )}
                  </>
                ) : (
                  /* Manual entry */
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                      {scope === "Region" ? "Enter your region / state" : "Enter your country"}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        value={manualInput}
                        onChange={e => setManualInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleManualSubmit()}
                        placeholder={scope === "Region" ? "e.g. California" : "e.g. United States"}
                        className="lb-search-input"
                        style={{
                          flex: 1, padding: "9px 12px", borderRadius: 10,
                          border: `1px solid ${C.border}`, background: C.bgCard,
                          color: C.text, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                        }}
                      />
                      <button onClick={handleManualSubmit} style={{
                        padding: "9px 16px", borderRadius: 10, border: "none",
                        background: C.accentGrad, color: "#fff", fontSize: 12, fontWeight: 700,
                        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                      }}>Go</button>
                    </div>
                  </>
                )}
              </div>
            )}

          </div>
        )}

        {/* ── LEADERBOARD LIST ── */}
        <div ref={listRef} onScroll={handleScroll} style={{ marginTop: 0 }}>
          {dataError ? (
            <div className="pr-error" role="alert"><p>{dataError}</p><button type="button" className="ps-button ps-button-secondary" onClick={() => { setDataError(""); fetchData(0); }}>Try again</button></div>
          ) : sportsLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 16 }}>
              {[...Array(8)].map((_, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 14, background: C.bgCard, border: `1px solid ${C.border}` }}>
                  <Skeleton w={32} h={32} style={{ borderRadius: "50%" }} />
                  <Skeleton w={42} h={42} style={{ borderRadius: "50%", flexShrink: 0 }} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                    <Skeleton w="60%" h={13} />
                    <Skeleton w="35%" h={10} />
                  </div>
                  <Skeleton w={60} h={13} />
                </div>
              ))}
            </div>
          ) : sportsError ? (
            <div role="alert" style={{ textAlign: "center", padding: "60px 20px", color: C.textMuted }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 8 }}>Leaderboard unavailable</div>
              <div style={{ fontSize: 13, marginBottom: 16 }}>{sportsError}</div>
              <button type="button" onClick={fetchActiveSports} style={{
                padding: "10px 18px", borderRadius: 12, border: "none",
                background: C.accentGrad, color: "#fff", fontSize: 13, fontWeight: 800,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              }}>Retry</button>
            </div>
          ) : !selectedSport ? (
            <div role="status" style={{ textAlign: "center", padding: "60px 20px", color: C.textMuted }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 8 }}>No supported sports</div>
              <div style={{ fontSize: 13 }}>There are no active sports available for the leaderboard right now.</div>
            </div>
          ) : loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 16 }}>
              {[...Array(8)].map((_, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 14, background: C.bgCard, border: `1px solid ${C.border}` }}>
                  <Skeleton w={32} h={32} style={{ borderRadius: "50%" }} />
                  <Skeleton w={42} h={42} style={{ borderRadius: "50%", flexShrink: 0 }} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                    <Skeleton w="60%" h={13} />
                    <Skeleton w="35%" h={10} />
                  </div>
                  <Skeleton w={60} h={13} />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: C.textMuted }}>
              <TrophyEmoji size={42} style={{ display: "block", marginBottom: 12 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>No athletes yet</div>
              <div style={{ fontSize: 13 }}>Be the first to compete in {selectedSport}!</div>
            </div>
          ) : (
            <div className="lb-stagger" style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 12 }}>
              {filtered.map((entry, i) => renderEntry(entry, i))}
            </div>
          )}

          {loadingMore && (
            <div style={{ textAlign: "center", padding: 20, color: C.textMuted, fontSize: 13 }}>Loading more…</div>
          )}
        </div>
      </div>

      {/* ════════════════ SEARCH MODAL ════════════════ */}
      {searchOpen && (
        <div className="lb-modal-overlay" onClick={() => setSearchOpen(false)} style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: isDark ? "rgba(0,0,0,0.75)" : "rgba(10,29,68,0.4)",
          backdropFilter: "blur(8px)",
          display: "flex", alignItems: "flex-start", justifyContent: "center",
          padding: "80px 16px 16px",
        }}>
          <div ref={modalRef} role="dialog" aria-modal="true" aria-label={searchOpen ? "Search leaderboard" : "Choose a community leaderboard"} className="lb-modal" onClick={e => e.stopPropagation()} style={{
            width: "min(600px, 100%)", background: C.bgCard,
            borderRadius: 20, border: `1px solid ${C.border}`,
            boxShadow: C.shadowLg, overflow: "hidden",
          }}>
            {/* Search bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
              <FontAwesomeIcon icon={faSearch} style={{ color: C.textMuted, fontSize: 14, flexShrink: 0 }} />
              <input
                ref={searchInputRef}
                className="lb-search-input"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                aria-label={`Search ${showTeams ? "teams" : "athletes"}`}
                placeholder={`Search ${showTeams ? "teams" : "athletes"}…`}
                style={{
                  flex: 1, background: "transparent", border: "none",
                  color: C.text, fontSize: 15, fontFamily: "'DM Sans', sans-serif",
                }}
              />
              <button aria-label="Close leaderboard search" onClick={() => setSearchOpen(false)} style={{
                background: "transparent", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 16,
              }}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {/* Results */}
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {searchResults.length === 0
                ? <div style={{ padding: 32, textAlign: "center", color: C.textMuted, fontSize: 13 }}>
                    {searchQuery ? "No results found." : "Start typing to search…"}
                  </div>
                : searchResults.map(entry => (
                    <div key={entry.id} className="lb-entry" role="button" tabIndex={0} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.currentTarget.click(); } }} onClick={() => {
                      setSearchOpen(false);
                      navigate(showTeams ? `/viewscreen/team/${entry.id}` : publicProfilePath(entry));
                    }} style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                      borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                    }}>
                      <img src={entry.profileImg || entry.teamImage || noImage} alt="" onError={e => e.target.src = noImage}
                        style={{ width: 44, height: 44, borderRadius: showTeams ? 8 : "50%", objectFit: "cover" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                          {showTeams ? entry.teamName : `${entry.name} ${entry.surname || ""}`.trim()}
                        </div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                          {showTeams ? `League ${entry.league}` : `@${entry.username}`}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: C.gold }}>
                        {isTrack ? formatPersonalBest(entry.personal_best) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <span>{(entry.trophies || 0).toLocaleString()}</span>
                            <TrophyEmoji size={10} />
                          </span>
                        )}
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      )}

      {/* ════════════════ INSTITUTE MODAL ════════════════ */}
      {instituteOpen && (
        <div className="lb-modal-overlay" onClick={() => setInstituteOpen(false)} style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: isDark ? "rgba(0,0,0,0.75)" : "rgba(10,29,68,0.4)",
          backdropFilter: "blur(8px)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}>
          <div ref={modalRef} role="dialog" aria-modal="true" aria-label={searchOpen ? "Search leaderboard" : "Choose a community leaderboard"} className="lb-modal" onClick={e => e.stopPropagation()} style={{
            width: "min(600px, 100%)", background: C.bgCard, borderRadius: "20px 20px 0 0",
            border: `1px solid ${C.border}`, boxShadow: C.shadowLg,
            maxHeight: "85vh", display: "flex", flexDirection: "column",
          }}>
            {/* Handle */}
            <div style={{ padding: "12px 20px 0", textAlign: "center" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 12px" }} />
              <div style={{ fontSize: 17, fontWeight: 800, color: C.text, fontFamily: "'Sora', sans-serif", marginBottom: 4 }}>Institutes</div>
            </div>

            <div style={{ padding: "12px 20px", overflowY: "auto", flex: 1 }}>
              {/* Join */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Join an Institute</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <input
                    value={accessCode}
                    onChange={e => { setAccessCode(e.target.value); setJoinError(""); }}
                    placeholder="Enter access code…"
                    className="lb-search-input"
                    style={{
                      flex: 1, padding: "11px 14px", borderRadius: 12,
                      border: `1.5px solid ${joinError ? C.danger : C.border}`,
                      background: C.bgAlt, color: C.text, fontSize: 13,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                  <button onClick={handleJoin} style={{
                    padding: "11px 20px", borderRadius: 12, border: "none",
                    background: C.accentGrad, color: "#fff", fontSize: 13, fontWeight: 700,
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  }}>Join</button>
                </div>
                {joinError && <div style={{ fontSize: 12, color: C.danger, marginTop: 6, fontWeight: 600 }}>{joinError}</div>}
              </div>

	              {/* Your institutes */}
	              <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 12 }}>Your Institutes</div>
	              {instituteModalGroups.length === 0
	                ? <div style={{ fontSize: 13, color: C.textMuted, textAlign: "center", padding: "20px 0" }}>You haven't joined any institutes yet.</div>
	                : instituteModalGroups.map(group => (
                    <div key={group.key} style={{ marginBottom: 18 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
                        {group.image
                          ? <img src={group.image} alt="" style={{ width: 28, height: 28, borderRadius: 8, objectFit: "contain", background: C.bgElevated, border: `1px solid ${C.border}` }} />
                          : <div style={{ width: 28, height: 28, borderRadius: 8, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <FontAwesomeIcon icon={faBuilding} style={{ color: C.accent, fontSize: 12 }} />
                            </div>
                        }
                        <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 900, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{group.title}</div>
                        <div style={{ minWidth: 24, textAlign: "center", borderRadius: 99, padding: "3px 7px", background: C.accentBgLight, color: C.accent, fontSize: 10, fontWeight: 900 }}>{group.rows.length}</div>
                      </div>
                      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch" }}>
                        {group.rows.map(inst => {
                          const isChallengeRow = isChallengeInstitute(inst);
                          const isGroupedRow = isGroupedInstitute(inst);
                          const rowLabel = isChallengeRow ? "Challenge" : isGroupedRow ? subgroupLabelForInstitute(inst) : "Members";
                          const rowIcon = isChallengeRow ? faFlag : isGroupedRow ? faBuilding : faUsers;
                          return (
                            <div key={inst.id} role="button" tabIndex={0} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.currentTarget.click(); } }} onClick={() => {
                              setInstituteOpen(false);
                              setShowInstitute(true);
                              fetchInstituteDetail(inst.id);
                            }} className="lb-entry" style={{
                              flex: "0 0 210px", minHeight: 148, display: "flex", flexDirection: "column", gap: 10, padding: 12,
                              borderRadius: 14, background: C.bgAlt, border: `1px solid ${C.border}`,
                              cursor: "pointer",
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                {inst.image
                                  ? <img src={inst.image} alt="" style={{ width: 42, height: 42, borderRadius: 10, objectFit: "contain", background: C.bgElevated, border: `1px solid ${C.border}` }} />
                                  : <div style={{ width: 42, height: 42, borderRadius: 10, background: C.accentBgSubtle, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                      <FontAwesomeIcon icon={faBuilding} style={{ color: C.accent, fontSize: 16 }} />
                                    </div>
                                }
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, lineHeight: 1.25, fontWeight: 900, color: C.text, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{inst.name}</div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{inst.state_province || inst.country || ""}</div>
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: "auto" }}>
                                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 26, borderRadius: 99, padding: "5px 8px", background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, color: C.accent, fontSize: 10, fontWeight: 900 }}>
                                  <FontAwesomeIcon icon={rowIcon} style={{ fontSize: 10 }} />
                                  {rowLabel}
                                </div>
                                <div style={{ textAlign: "right" }}>
                                  <div style={{ fontSize: 12, fontWeight: 900, color: C.gold }}>{(inst.steps || 0).toLocaleString()}</div>
                                  <div style={{ fontSize: 9, color: C.textMuted }}>steps</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
	              }
            </div>

            <div style={{ padding: "12px 20px 24px" }}>
              <button aria-label="Close institution details" onClick={() => setInstituteOpen(false)} style={{
                width: "100%", padding: 13, borderRadius: 12, border: `1px solid ${C.border}`,
                background: C.bgAlt, color: C.text, fontSize: 14, fontWeight: 700,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </PublicSiteLayout>
  );
};

export default Leaderboard;
